import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import Radium from 'radium';
import Translate from '../../../../../components/Translate';
import config from '../../../../../config';
import ClockArrowUpIcon from "../../../../../assets/icons/ClockArrowUpIcon";
import BotIcon from "../../../../../assets/icons/BotIcon";
import '../chat-css/ChatCard.css';
import {getContactFromConversation} from '../../../../../utils/ComponentUtilFunctions';
import {convertFormBackendDate} from '../../../../../utils/General';
import ContextMenu from "../../../../../components-new/ui/ContextMenu";
import {getUserByEmails} from "../../../../../services/company/api";
import PlatformIcon from "../../../../../components-new/general/PlatformIcon";
import ContactAvatar from "../../../../../components-new/general/ContactAvatar";
import ChatMoreInfoModal from "../../../../../components-new/general/ChatMoreInfoModal";
import BlockUserModal from "./BlockUserModal";
import EndConversationModal from "./EndConversationModal";
import TicketModal from "./TicketModal";
import ForwardDialogModal from "./ForwardDialogModal";
import Colors from "../../../../../utils/Colors";
import ForwardIcon from "../../../../../assets/icons/ForwardIcon";
import InfoIcon from "../../../../../assets/icons/InfoIcon";
import NotebookPenIcon from "../../../../../assets/icons/NotebookPenIcon";
import PowerIcon from "../../../../../assets/icons/PowerIcon";
import ClockAlertIcon from "../../../../../assets/icons/ClockAlertIcon";
import GalleryHorizontalEndIcon from "../../../../../assets/icons/GalleryHorizontalEndIcon";
import BriefcaseBusinessIcon from "../../../../../assets/icons/BriefcaseBusinessIcon";
import PersonOffIcon from "../../../../../assets/icons/PersonOffIcon";
import PersonRemoveIcon from "../../../../../assets/icons/PersonRemoveIcon";
import PersonIcon from "../../../../../assets/icons/PersonIcon";
import TableDateTime from "../../../../../components-new/table/TableDateTime";
import Tooltip from "../../../../../components-new/general/Tooltip";

/** Whatever the operator typed into this chat and has not sent. */
function readDraft(conversation) {
    try {
        return !!sessionStorage.getItem(conversation.id);
    } catch (e) {
        return false;
    }
}

/*
 * A card in the list. Every one of them used to be a pale blue tile, so the
 * whole column read as highlighted and the one chat actually selected had only
 * a stripe to say so. White cards on the panel's grey, and the brand tint kept
 * for the selected one.
 */
class ChatUserListContact extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            showContextMenu: false,
            contextMenuPosition: {x: 0, y: 0},
            dialogState: {isOpen: false, mode: ''},
            members: [],
            isInnerModalOpen: false,
            isBlockUserModalOpen: false,
            isEndConversationModalOpen: false,
            isTicketModalOpen: false,
            flash: false,
            hasDraft: readDraft(props.conversation),
            draftLeaving: false,
        };
    }

    /*
     * Something arrived in a chat that is not open.
     *
     * The count in the corner changes, which is a number changing - easy to
     * miss in a column of twenty cards. The card says it once, with a light
     * that runs across it and goes out.
     */
    componentDidUpdate(prevProps) {
        const {conversation, isSelected} = this.props;

        const before = prevProps.conversation.unread || 0;
        const after = conversation.unread || 0;

        if (after > before && !isSelected && !this.state.flash) {
            this.setState({flash: true});
            this.flashTimer = setTimeout(() => {
                if (!this.unmounted) this.setState({flash: false});
            }, 1000);
        }

        /*
         * Session storage says nothing when it changes, so the draft is read
         * again whenever the card is - which is every message, every selection
         * and every turn of the list's clock.
         *
         * Appearing is immediate; leaving takes as long as the collapse, so the
         * chip is still there to be animated out.
         */
        const hasDraft = readDraft(conversation);

        if (hasDraft && (!this.state.hasDraft || this.state.draftLeaving)) {
            clearTimeout(this.draftTimer);
            this.setState({hasDraft: true, draftLeaving: false});
        } else if (!hasDraft && this.state.hasDraft && !this.state.draftLeaving) {
            this.setState({draftLeaving: true});
            this.draftTimer = setTimeout(() => {
                if (!this.unmounted) this.setState({hasDraft: false, draftLeaving: false});
            }, 240);
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        clearTimeout(this.flashTimer);
        clearTimeout(this.draftTimer);
    }

    openDialog = (mode) => {
        this.setState({showContextMenu: false})

        const {conversation} = this.props;

        let filteredMembers = [];
        if (conversation.platform !== config.platformsMap.company) {
            filteredMembers = this.filterMembersToForward();
        }

        getUserByEmails(filteredMembers.map(m => m.email)).then(data => {
            const updatedData = data.map(user => ({
                ...user,
                id: user.platformId
            }));
            const sortedData = updatedData.sort((a, b) => a.currentStatus.localeCompare(b.currentStatus));
            this.setState({members: sortedData});
        });

        this.setState({dialogState: {isOpen: true, mode}});
    };

    closeDialog = () => {
        this.setState({dialogState: {isOpen: false, mode: ''}});
    };

    handleContextMenu = (event) => {
        event.preventDefault();

        const x = event.clientX;
        const y = event.clientY;

        this.setState({
            showContextMenu: false
        }, () => {
            this.setState({
                showContextMenu: true,
                contextMenuPosition: {x, y}
            });
        });

    };

    filterMembersToForward = () => {
        const {conversation: {companyId}, company: {members}, user} = this.props;
        const {statusMap} = config;
        return members.filter(member =>
            member.id !== user.id && member.status === statusMap.enabled && (member.subCompanyIds.includes(companyId) || member.authorities[0] === config.rolesMap.admin || member.authorities[0] === config.rolesMap.owner));
    };

    openInfoModal = () => {
        this.setState({showContextMenu: false})
        this.setState({
            isInnerModalOpen: true
        });
    };

    closeInfoModal = () => {
        this.setState({
            isInnerModalOpen: false
        });
    };

    openBlockUserModal = () => {
        this.setState({showContextMenu: false})
        this.setState({
            isBlockUserModalOpen: true
        });
    };

    closeBlockUserModal = () => {
        this.setState({
            isBlockUserModalOpen: false
        });
    };

    openEndConversationModal = () => {
        this.setState({showContextMenu: false})
        this.setState({
            isEndConversationModalOpen: true
        });
    };

    closeEndConversationModal = () => {
        this.setState({
            isEndConversationModalOpen: false
        });
    };

    openTicketModal = () => {
        this.setState({showContextMenu: false})
        this.setState({
            isTicketModalOpen: true
        });
    };

    closeTicketModal = () => {
        this.setState({
            isTicketModalOpen: false
        });
    };

    showConversationHistory = (conversationInfo) => {
        const {selectConversation, conversation} = this.props;
        selectConversation({
            id: conversationInfo.id,
            companyId: conversationInfo.companyId,
            platform: conversationInfo.platform,
            userFirstName: conversationInfo.userFirstName,
            userLastName: conversationInfo.userLastName,
            userThumbUrl: conversationInfo.userThumbUrl,
            visitorEmail: conversationInfo.visitorEmail,
            visitorPhoneNumber: conversationInfo.visitorPhoneNumber,
            fromChatContact: true,
            fullConversationForSelect: conversation,
        }, false, false); // without take
    };

    render() {
        const {
            conversation,
            selectConversation,
            isSelected,
            isSupervisor,
            user,
            forwardConversation,
            company,
        } = this.props;

        const {
            dialogState,
            members,
            isInnerModalOpen,
            isBlockUserModalOpen,
            isEndConversationModalOpen,
            isTicketModalOpen
        } = this.state;

        let features = []
        if (company) {
            features = company.tariff.features
        }

        const contact = getContactFromConversation(conversation);
        const lastMessage = {
            type: conversation.type,
            text: conversation.text,
            date: conversation.date,
            attachmentId: conversation.attachmentId,
        };

        const firstName = contact.firstName || '';
        const lastName = contact.lastName || '';
        const messageDate = lastMessage.date ? convertFormBackendDate(lastMessage.date) : null;
        let contactText;
        if (lastMessage.type == null) {
            contactText = <Translate text="noMessages"/>;
        } else if (lastMessage.type === 'TEXT' || lastMessage.type === 'QUICK_REPLY') {
            contactText = lastMessage.text;
        } else {
            contactText = <Translate text="attachment"/>;
        }

        const hasUnReadMessages = !!conversation.unread;
        const isUnmanagedChat = conversation.status === config.conversationStatuses.unManaged;

        /* an answer typed and not sent - MessageInput keeps it under the chat's id */
        const {hasDraft, draftLeaving} = this.state;

        /*
         * What kind of chat this is, as chips. These were five lines of 9px
         * "label: value" stacked under the name, which is why the card needed
         * 156px of height and still had to clip most of them.
         */
        const chips = [];
        if (hasDraft) {
            chips.push({
                key: 'draft',
                tone: 'brand',
                className: draftLeaving ? 'chat-chip--leaving' : 'chat-chip--entering',
                icon: <NotebookPenIcon strokeWidth={2.5} width={10} height={10}/>,
                label: <Translate text="unsentDraft"/>,
            });
        }
        if (conversation.afk) {
            chips.push({
                key: 'afk',
                tone: 'alert',
                icon: <ClockAlertIcon strokeWidth={2.5} width={10} height={10}/>,
                label: <Translate text="afkChat"/>,
            });
        }
        if (conversation.queued) {
            chips.push({
                key: 'queued',
                icon: <GalleryHorizontalEndIcon strokeWidth={2.5} width={10} height={10}/>,
                label: <Translate text="inQueue"/>,
            });
        }
        if (conversation.outsideWorkingHours) {
            chips.push({
                key: 'hours',
                icon: <BriefcaseBusinessIcon strokeWidth={2.5} width={10} height={10}/>,
                label: <Translate text="outOfWorkingHoursRequest"/>,
            });
        }
        if (conversation.companyName && conversation.companyName !== "null") {
            chips.push({
                key: 'bot',
                icon: <BotIcon strokeWidth={2.5} width={10} height={10}/>,
                label: conversation.companyName,
            });
        }
        /*
         * Whose chat this is - and, for an unmanaged one, whose it *was*.
         *
         * The field is lastOperatorName in both cases, so a bare name on the
         * card read as "this operator is on it" even when nobody was: an
         * unmanaged chat is waiting for someone precisely because that operator
         * has left it. The two are different chips now.
         *
         * Supervisor: a brand chip with the person on the chat right now.
         * Unmanaged: a muted chip saying the name is the last one, not the
         * current one - and it only appears when there is a name to show.
         */
        const operatorName = conversation.lastOperatorName;
        const hasOperatorName = operatorName && operatorName.trim() !== '';

        if (isUnmanagedChat) {
            chips.push({
                key: 'unmanaged',
                tone: 'warning',
                icon: <PersonRemoveIcon width={10} height={10}/>,
                label: <Translate text="noOperator"/>,
            });

            if (hasOperatorName) {
                chips.push({
                    key: 'last-operator',
                    icon: <ClockArrowUpIcon strokeWidth={2.5} width={10} height={10}/>,
                    label: <span><Translate text="lastOperator"/>: {operatorName}</span>,
                });
            }
        } else if (isSupervisor && hasOperatorName) {
            chips.push({
                key: 'operator',
                tone: 'brand',
                icon: <PersonIcon width={10} height={10}/>,
                label: operatorName,
            });
        }

        /*
         * The visitor's own details. They do not belong on a card that has to be
         * scanned - they are read when a chat is opened - but they are still
         * worth having to hand, so they sit in the title of the name.
         */
        const details = [`${firstName} ${lastName}`.trim()];
        if (contact.visitorEmail && contact.visitorEmail !== "null") details.push(contact.visitorEmail);
        if (contact.visitorPhoneNumber && contact.visitorPhoneNumber !== "null") details.push(contact.visitorPhoneNumber);

        const cardClasses = ['chat-card'];
        if (isSelected) cardClasses.push('selected');
        if (conversation.afk) cardClasses.push('afk');
        if (this.state.flash) cardClasses.push('chat-card--flash');

        return (
            <React.Fragment>
                <li
                    role="menuitem"
                    onContextMenu={this.handleContextMenu}
                    onClick={() => {
                        if (conversation.platform === config.platformsMap.company) {
                            selectConversation(conversation);
                            return
                        }

                        const isOwnerOrAdmin =
                            user.authorities.includes(config.rolesMap.owner) ||
                            user.authorities.includes(config.rolesMap.admin);

                        const isUnmanaged = conversation.status === config.conversationStatuses.unManaged;

                        const baseData = {
                            ...conversation,
                            fullConversationForSelect: undefined,
                            isSupervisor
                        };
                        if (isOwnerOrAdmin) {
                            if (isUnmanaged) {
                                this.showConversationHistory(conversation);
                            } else {
                                selectConversation(baseData, false, false);
                            }
                        } else {
                            const shouldTake = isUnmanaged;
                            selectConversation(baseData, false, shouldTake);
                        }
                    }}
                    className={cardClasses.join(' ')}
                >
                    <div
                        className={`chat-card__avatar ${conversation.platform !== config.platformsMap.company ? 'chat-card__avatar--platform' : ''}`}>
                        {conversation.platform === config.platformsMap.company ? (
                            <ContactAvatar contact={contact} variant="chatMenu" size={40}/>
                        ) : (
                            <PlatformIcon conversation={conversation} size={40}/>
                        )}
                    </div>

                    <div className="chat-card__body">
                        <div className="chat-card__top">
                            <Tooltip text={details.join(' · ')} needTranslate={false}
                                     style={{minWidth: 0, flex: 1}}>
                                <span className="chat-card__name">{firstName} {lastName}</span>
                            </Tooltip>
                            {messageDate && (
                                <span className="chat-card__time">
                                    <TableDateTime value={messageDate}/>
                                </span>
                            )}
                        </div>

                        <div className="chat-card__bottom">
                            <span className="chat-card__preview">{contactText}</span>
                            {hasUnReadMessages && (
                                <span className="chat-card__unread" key={conversation.unread}>
                                    {conversation.unread}
                                </span>
                            )}
                        </div>

                        {chips.length > 0 && (
                            <div className="chat-card__chips">
                                {chips.map(chip => (
                                    <span
                                        key={chip.key}
                                        className={'chat-chip'
                                            + (chip.tone ? ' chat-chip--' + chip.tone : '')
                                            + (chip.className ? ' ' + chip.className : '')}
                                    >
                                        {chip.icon && <span className="chat-chip__icon">{chip.icon}</span>}
                                        <span className="chat-chip__label">{chip.label}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </li>

                {conversation.platform !== config.platformsMap.company && this.state.showContextMenu && (
                    <ContextMenu
                        position={this.state.contextMenuPosition}
                        onClose={() => this.setState({showContextMenu: false})}
                        items={[
                            {
                                key: 'info',
                                label: <Translate text="info"/>,
                                icon: <InfoIcon strokeWidth={1.5} height={20} width={20}
                                                style={{color: Colors.neutral600}} stroke="currentColor"/>,
                                onClick: this.openInfoModal
                            },
                            {
                                key: 'ticket',
                                label: <Translate text="notes"/>,
                                icon: <NotebookPenIcon strokeWidth={1.5} height={20} width={20}
                                                       style={{color: Colors.neutral600}} stroke="currentColor"/>,
                                onClick: this.openTicketModal
                            },
                            ...(features.includes(config.features.CONVERSATION_FORWARD) ? [
                                {
                                    key: 'forward',
                                    label: <Translate text="forward"/>,
                                    icon: <ForwardIcon height={20} width={20} style={{color: Colors.neutral600}}
                                                       stroke="currentColor"/>,
                                    onClick: this.openDialog
                                },
                                {
                                    key: 'after-forward-divider',
                                    isDivider: true
                                }
                            ] : []),
                            ...(features.includes(config.features.BLACK_LIST) ? [{
                                key: 'block',
                                danger: true,
                                label: <Translate text="blockUser"/>,
                                icon: <PersonOffIcon height={20} width={20} style={{color: Colors.neutral600}}/>,
                                onClick: this.openBlockUserModal
                            }] : []),
                            {
                                key: 'end',
                                danger: true,
                                label: <Translate text="endConversation"/>,
                                icon: <PowerIcon strokeWidth={1.5} height={20} width={20}
                                                 style={{color: Colors.neutral600}} stroke="currentColor"/>,
                                onClick: this.openEndConversationModal
                            },
                        ]}
                    />
                )}

                {conversation.platform !== config.platformsMap.company && dialogState.isOpen && (
                    <ForwardDialogModal
                        conv={conversation}
                        state={dialogState}
                        close={this.closeDialog}
                        contactList={members}
                        contactClick={forwardConversation}
                        user={user}
                        conversation={conversation}
                    />
                )}

                {isInnerModalOpen && (
                    <ChatMoreInfoModal
                        isOpen={isInnerModalOpen}
                        onRequestClose={this.closeInfoModal}
                        company={company}
                        conversation={conversation}
                    />
                )}

                {isBlockUserModalOpen && (
                    <BlockUserModal
                        isOpen={isBlockUserModalOpen}
                        cancelAction={this.closeBlockUserModal}
                        conversation={conversation}
                    />
                )}

                {isEndConversationModalOpen &&
                    <EndConversationModal
                        features={features}
                        isOpen={isEndConversationModalOpen}
                        cancelAction={this.closeEndConversationModal}
                        conversation={conversation}
                        user={user}
                    />
                }

                {isTicketModalOpen &&
                    <TicketModal
                        features={features}
                        isOpen={isTicketModalOpen}
                        cancelAction={this.closeTicketModal}
                        conversation={conversation}
                        user={user}
                        company={company}
                    />
                }

            </React.Fragment>
        );
    }
}

ChatUserListContact.propTypes = {
    conversation: PropTypes.object.isRequired,
    isSelected: PropTypes.bool.isRequired,
    selectConversation: PropTypes.func.isRequired,
    isSupervisor: PropTypes.bool,
    user: PropTypes.object,
};

export default Radium(ChatUserListContact);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/ChatContact.jsx